import { Injectable } from '@angular/core';
import {
  DataEntity,
  DataEntityDto,
  DataEntityExtensions,
  DataEntityField,
  DataEntityTimelineContributionOptions,
  InternalEntity,
} from '@osos/shared/api';
import {
  ApplicationSharingLevelEnum,
  AutomatedActionTypeEnum,
  DataEntityPresenterModel,
  DataEntityTypeEnum,
  DataTypeEnum,
  EventTypeEnum,
  FieldPresenterModel,
  InternalCollectionPresenterModel,
  LiteDataModulePresenterModel,
  ReactionDataEntityPresenterModel,
} from '@osos/shared/types';
import { ParserBase } from '../../bases/parser/parser-base';
import { DataReactionWithRelationsFactoryService } from '../../factories/data-entity-trigger-factory/data-entity-trigger-factory.service';
import { DataEntityWorkflowFactoryService } from '../../factories/data-entity-workflow-factory/data-entity-workflow-factory.service';
import { FieldFactoryService } from '../../factories/field-factory/field-factory.service';
import { InternalEntityFactoryService } from '../../factories/internal-entity-factory/internal-entity-factory.service';

import {
  DataEntityExtensionPresenterModel,
  InternalEntityPresenterModel,
  LocalizedValueModel,
  TimelineContributionOptionsPresenterModel,
} from '@osos/shared/types';
import cloneDeep from 'lodash-es/cloneDeep';
import omit from 'lodash-es/omit';
import { v4 as uuidv4 } from 'uuid';
import { DataEntityTemplateFactoryService } from '../../factories';

@Injectable()
export class EntityParserV1Service extends ParserBase<
  DataEntity,
  DataEntityPresenterModel
> {
  constructor(
    private dataEntityWorkflowFactoryService: DataEntityWorkflowFactoryService,
    private internalEntityFactoryService: InternalEntityFactoryService,
    private dataEntityTriggerFactoryService: DataReactionWithRelationsFactoryService,
    private dataEntityTemplateFactoryService: DataEntityTemplateFactoryService,
    private fieldFactory: FieldFactoryService,
  ) {
    super(DataEntity, DataEntityPresenterModel, 1);
  }

  getPresenterModel(
    model: Partial<DataEntity & DataEntityDto>,
  ): DataEntityPresenterModel {
    const presenter = {
      specificationVersion: this.version,
    } as DataEntityPresenterModel;

    presenter.id = model.id;
    presenter.name = model.name;
    presenter.description = (model.description as LocalizedValueModel) ?? null;
    presenter.formSettings = model?.formSettings;
    presenter.icon = model.icon;

    const presenterFields = [];
    const presentedMetaFields = [];
    const presentedInternalEntities = [];
    for (const field of model.fields ?? []) {
      const presentedField = this.fieldFactory.createPresenterModel(field);
      if (field.block?.internalEntity) {
        const internalEntity = model.internalEntities.find(
          (internal) => internal.id === field.block.internalEntity,
        );
        const presentedInternalEntity = this._mapInternalEntity(
          internalEntity,
          model.internalCollections,
          field,
        );

        presentedField.block = { internalEntity: presentedInternalEntity };

        presentedInternalEntities.push(presentedInternalEntity);
      } else {
        presentedField.block = { internalEntity: null };
      }
      if (field.renderOptions.editComponent.component === 'SwitchComponent') {
        const switchFieldOptions = this.getSwitchFieldOptions(
          presentedField,
          model.internalCollections,
        );

        presentedField.renderOptions = {
          ...presentedField.renderOptions,
          editComponent: {
            ...presentedField.renderOptions.editComponent,
            options: {
              ...presentedField.renderOptions.editComponent.options,
              options: switchFieldOptions,
            },
          },
        };
      }
      if (field.sourceOptions?.display?.relatedField?.length) {
        const internalEntity = model.internalEntities.find(
          (internal) => internal.id === `REF-${field.id}`,
        );
        const presentedInternalEntity = this._mapInternalEntity(
          internalEntity,
          model.internalCollections,
        );
        presentedInternalEntities.push(presentedInternalEntity);
      }
      presenterFields.push(presentedField);
    }

    for (const metaField of model.metaFields ?? []) {
      presentedMetaFields.push(
        this.fieldFactory.createPresenterModel(metaField),
      );
    }

    presenter.templates =
      model.templates?.map((template) =>
        this.dataEntityTemplateFactoryService.createPresenterModel(template),
      ) ?? [];
    presenter.fields = presenterFields;
    presenter.internalEntities = presentedInternalEntities;
    presenter.normalizedName = model.normalizedName;
    presenter.state = model?.state;
    presenter.isImplicit = model.isImplicit;
    presenter.type = model.type as DataEntityTypeEnum;
    presenter.indexes = model.indexes;
    presenter.typeOptions = model.typeOptions;
    presenter.typeDetails = model.typeDetails;
    presenter.dataSources = model.dataSources;
    presenter.storeRules = model.storeRules;
    presenter.workflows = model.workflows?.map((workflow) =>
      this.dataEntityWorkflowFactoryService.createPresenterModel(workflow),
    );
    // presenter.triggers =
    //   model.triggers?.map((trigger) =>
    //     this.dataEntityTriggerFactoryService.createPresenterModel(trigger),
    //   ) ?? [];
    presenter.meta = model.meta;
    presenter.module = model.module;
    presenter.moduleDetails =
      model.moduleDetails as LiteDataModulePresenterModel;
    presenter.entriesCount = model.entriesCount;
    presenter.displayOption = model.displayOption;

    presenter.extentions = this._getExtensionsPresenterModel(model?.extentions);

    presenter.version = model.version;
    presenter.entryNames = model?.entryNames;
    presenter.implicitOptions = model?.implicitOptions
      ? model?.implicitOptions
      : {
          allowEdit: true,
          isReadOnly: false,
        };
    presenter.virtual = model?.virtual;
    presenter.treeStructure = {
      supported: model?.treeStructure?.supported,
      fields: model?.treeStructure?.fields,
    };
    presenter.internalCollections = model.internalCollections;
    presenter.sharing = {
      ...model.sharing,
      level: model.sharing?.level?.id as ApplicationSharingLevelEnum,
    };
    presenter.updateIdentifier = model.updateIdentifier;
    presenter.metaFields = presentedMetaFields;
    presenter.displayTemplate = model.displayTemplate;
    return presenter;
  }
  getDataModel(presenter: DataEntityPresenterModel): DataEntity {
    const model = {
      specificationVersion: this.version,
      icon: {
        name: presenter.icon.name,
      },
      isImplicit: presenter.isImplicit,
      name: presenter.name,
      indexes: presenter.indexes,
      normalizedName: presenter.normalizedName,
      type: presenter.type,
      description: presenter.description,
      meta: presenter.meta,
      module: presenter.module,
      permissions: presenter.permissions,
      entryNames: presenter?.entryNames,
      formSettings: presenter.formSettings,
      typeOptions: presenter.typeOptions ?? undefined,
      workflows: [
        ...presenter.workflows?.map((w) =>
          this.dataEntityWorkflowFactoryService.createDataModel(w),
        ),
      ],
      // triggers: presenter.triggers?.map((t) =>
      //   this.dataEntityTriggerFactoryService.createDataModel(t),
      // ),
      virtual: presenter?.virtual as DataEntity['virtual'],

      displayOption: presenter.displayOption,
      extentions: presenter?.extentions
        ? this._getExtensionsDataModel(presenter.extentions)
        : undefined,
      templates: presenter?.templates
        ? presenter.templates?.map((presenterTemplate) =>
            this.dataEntityTemplateFactoryService.createDataModel(
              presenterTemplate,
            ),
          )
        : undefined,
      storeRules: presenter.storeRules ?? undefined,
      updateIdentifier: presenter.updateIdentifier,
    } as DataEntity;
    const internalCollections = [];
    const mapSwitchField = (field) => {
      const mappedField = cloneDeep(field);
      if (
        mappedField.renderOptions.editComponent.component ===
          'SwitchComponent' &&
        mappedField.renderOptions.editComponent.options?.options?.length
      ) {
        const internalCollectionId =
          mappedField.dataCollection || `internal/${uuidv4()}`;
        const originalInternalCollection = presenter.internalCollections.find(
          (c) => c.id === internalCollectionId,
        );
        const items = (
          mappedField.renderOptions.editComponent.options as any
        ).options.map((option, index) => {
          return {
            id: option.id || uuidv4(),
            value: omit(option, ['id', 'isDeleted']),
            index,
            isDeleted: option.isDeleted ?? false,
          };
        });
        if (originalInternalCollection) {
          originalInternalCollection.items.forEach((item) => {
            if (!items.find((i) => i.id === item.id)) {
              // item is removed from the options
              items.push({
                ...item,
                isDeleted: true,
              });
            }
          });
        }
        internalCollections.push({
          id: internalCollectionId,
          items,
          dataEntity: 'ADTString',
        });
        mappedField.renderOptions.editComponent.options = omit(
          field.renderOptions.editComponent.options,
          'options',
        );
        mappedField.dataCollection = internalCollectionId;
        mappedField.dataType = DataTypeEnum.Json;
        if (
          mappedField.renderOptions.editComponent.options.switchType !== 2 &&
          !mappedField.options.multiValue
        ) {
          mappedField.options.multiValue = {
            min: 1,
          };
        } else {
          if (
            mappedField.renderOptions.editComponent.options.switchType === 2
          ) {
            mappedField.options.multiValue = null;
          }
        }
      }
      return this.fieldFactory.createDataModel(mappedField);
    };
    model.fields = presenter.fields.map(mapSwitchField);
    model.internalEntities = presenter.internalEntities
      ?.filter((ie) => !ie.id.startsWith('REF-'))
      .map((entity) => {
        const mappedEntity =
          this.internalEntityFactoryService.createDataModel(entity);
        mappedEntity.fields = entity.fields.map(mapSwitchField);
        return mappedEntity;
      });
    model.internalCollections = internalCollections;
    model.displayTemplate = presenter.displayTemplate;

    return model;
  }

  getPresenterModelForReactionEntity(
    model: Partial<
      DataEntity &
        DataEntityDto & {
          reactionActionType?: AutomatedActionTypeEnum;
          reactionEventType?: EventTypeEnum;
          reactionEntityId?: string;
        }
    >,
  ): ReactionDataEntityPresenterModel {
    const basePresenter = this.getPresenterModel(
      model,
    ) as ReactionDataEntityPresenterModel;

    basePresenter.reactionActionType = model.reactionActionType;
    basePresenter.reactionEventType = model.reactionEventType;
    basePresenter.reactionEntityId = model.reactionEntityId;

    return basePresenter;
  }

  private getSwitchFieldOptions(
    field: FieldPresenterModel,
    internalCollections: InternalCollectionPresenterModel[],
  ) {
    const collection = internalCollections.find(
      (c) => c.id === field.dataCollection,
    );
    if (!collection) {
      return [];
    }
    return collection.items.map((collectionItem) => {
      return {
        ...collectionItem.value,
        id: collectionItem.id,
        isDeleted: collectionItem.isDeleted,
      };
    });
  }
  private _mapInternalEntity(
    internalEntity: InternalEntity,
    internalCollections: InternalCollectionPresenterModel[],
    blockField?: DataEntityField,
  ): InternalEntityPresenterModel {
    if (internalEntity) {
      const _presentedInternalEntity =
        this.internalEntityFactoryService.createPresenterModel(internalEntity);
      _presentedInternalEntity.fields = _presentedInternalEntity.fields.map(
        (field) => {
          if (
            field.renderOptions.editComponent.component === 'SwitchComponent'
          ) {
            field.renderOptions.editComponent.options.options =
              this.getSwitchFieldOptions(field, internalCollections);
          }
          return {
            ...field,
            _normalizedName: blockField
              ? `${blockField.normalizedName}.${field.normalizedName}`
              : field['fullNormalizedName'],
          };
        },
      );
      return _presentedInternalEntity;
    }
    return undefined;
  }

  private _getExtensionsPresenterModel(
    extensions?: DataEntityExtensions,
  ): DataEntityExtensionPresenterModel {
    return {
      timelineContribution: extensions?.timelineContribution
        ? this._getTimelinePresenterModel(extensions.timelineContribution)
        : null,
      realtimeConfig: extensions?.realtimeConfig ?? {
        enabled: false,
      },
    };
  }

  private _getExtensionsDataModel(
    extensions?: DataEntityExtensionPresenterModel,
  ): DataEntityExtensions {
    return {
      timelineContribution: extensions
        ? this._getTimelineDataModel(extensions.timelineContribution)
        : undefined,
      realtimeConfig: extensions?.realtimeConfig,
    };
  }

  private _getTimelineDataModel(
    presenter: TimelineContributionOptionsPresenterModel,
  ): DataEntityTimelineContributionOptions {
    if (!presenter) {
      return undefined;
    }
    return presenter;
  }

  private _getTimelinePresenterModel(
    model: DataEntityTimelineContributionOptions,
  ): TimelineContributionOptionsPresenterModel {
    const presenter: TimelineContributionOptionsPresenterModel = model;
    return presenter;
  }
}
